'use client';

import { useSiteConfig } from '@/context/SiteConfigContext';
import { siteConfig as seoFallback } from '@/lib/seo-config';

/**
 * Helper: Build an array of social media URLs from the SiteConfig social object.
 * Filters out empty strings so only configured platforms appear.
 */
function buildSocialUrls(social: {
  facebook: string;
  instagram: string;
  twitter: string;
  youtube: string;
  pinterest: string;
  linkedin: string;
}): string[] {
  return [
    social.facebook,
    social.twitter,
    social.instagram,
    social.pinterest,
    social.youtube,
    social.linkedin,
  ].filter(Boolean);
}

// Organization Schema
export function OrganizationJsonLd() {
  const cfg = useSiteConfig();

  // Derive values from context with seo-config fallback
  const name = cfg.companyName || seoFallback.name;
  const legalName = cfg.companyLegalName || name;
  const url = cfg.siteUrl || seoFallback.url;
  const description = cfg.description || seoFallback.description;
  const phone = cfg.phone || seoFallback.phone;
  const logoUrl = cfg.logoUrl
    ? (cfg.logoUrl.startsWith('http') ? cfg.logoUrl : `${url}${cfg.logoUrl}`)
    : `${url}${seoFallback.logo}`;

  const socialUrls = buildSocialUrls(cfg.social);

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${url}/#organization`,
    name,
    legalName,
    alternateName: 'Smart Blinds™ USA',
    url,
    logo: logoUrl,
    description,
    address: {
      '@type': 'PostalAddress',
      ...(cfg.address.street && { streetAddress: cfg.address.street }),
      ...(cfg.address.city && { addressLocality: cfg.address.city }),
      ...(cfg.address.state && { addressRegion: cfg.address.state }),
      ...(cfg.address.zip && { postalCode: cfg.address.zip }),
      addressCountry: cfg.address.country || 'US',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: phone,
        contactType: 'customer service',
        availableLanguage: ['English'],
        areaServed: 'US',
      },
      {
        '@type': 'ContactPoint',
        telephone: phone,
        contactType: 'sales',
        availableLanguage: ['English'],
        areaServed: 'US',
      },
    ],
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    brand: {
      '@type': 'Brand',
      name: 'Smart Blinds™',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `${name} Product Line`,
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Custom Motorized Blinds Manufacturing',
          },
          areaServed: 'US',
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Professional Window Treatment Installation',
          },
          areaServed: 'US',
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Window Treatment Consultation',
          },
          areaServed: 'US',
        },
      ],
    },
    sameAs: socialUrls,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Website Schema
export function WebsiteJsonLd() {
  const cfg = useSiteConfig();

  const name = cfg.companyName || seoFallback.name;
  const url = cfg.siteUrl || seoFallback.url;
  const description = cfg.description || seoFallback.description;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${url}/#website`,
    url,
    name,
    description,
    publisher: {
      '@id': `${url}/#organization`,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${url}/products?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'en-US',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Local Business Schema
// IMPORTANT: address + geo are required for Google "near me" results
export function LocalBusinessJsonLd() {
  const cfg = useSiteConfig();

  const name = cfg.companyName || seoFallback.name;
  const url = cfg.siteUrl || seoFallback.url;
  const phone = cfg.phone || seoFallback.phone;
  // Public business email = sales/contact address from the DB (e.g.
  // sales@smartblindspro.com), not the internal support inbox. DB-driven.
  const email = cfg.email?.sales || cfg.email?.support || seoFallback.email;
  const logoUrl = cfg.logoUrl
    ? (cfg.logoUrl.startsWith('http') ? cfg.logoUrl : `${url}${cfg.logoUrl}`)
    : `${url}${seoFallback.logo}`;

  // Address: prefer database values, fall back to registered office address
  const streetAddress = cfg.address?.street || '15326 Old Redmond Rd';
  const addressLocality = cfg.address?.city || 'Redmond';
  const addressRegion = cfg.address?.state || 'WA';
  const postalCode = cfg.address?.zip || '98052';

  const schema = {
    '@context': 'https://schema.org',
    '@type': ['HomeAndConstructionBusiness', 'Store'],
    '@id': `${url}/#localbusiness`,
    name,
    alternateName: 'Smart Blinds™ USA',
    description: 'Smart Blinds™ offers premium custom window blinds, shades, and motorized window treatments. Patent-pending Smart Defense technology. Serving Redmond WA and shipping nationwide across 3,200+ zip codes. Free AR visualization tool to preview blinds in your home before buying.',
    image: logoUrl,
    logo: logoUrl,
    url,
    telephone: phone,
    email,
    priceRange: '$$',

    // ← CRITICAL for "near me" results
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality,
      addressRegion,
      postalCode,
      addressCountry: 'US',
    },

    // ← Geo coordinates for map pack ranking (15326 Old Redmond Rd, Redmond WA)
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 47.6868,
      longitude: -122.1162,
    },

    // ← Links to Google Maps listing
    hasMap: `https://www.google.com/maps/search/?api=1&query=Smart+Blinds+15326+Old+Redmond+Rd+Redmond+WA+98052`,

    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '20:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '09:00',
        closes: '17:00',
      },
    ],

    // National e-commerce + local Redmond service area
    areaServed: [
      {
        '@type': 'Country',
        name: 'United States',
      },
      {
        '@type': 'City',
        name: 'Redmond',
        containedInPlace: {
          '@type': 'State',
          name: 'Washington',
        },
      },
      {
        '@type': 'City',
        name: 'Bellevue',
        containedInPlace: { '@type': 'State', name: 'Washington' },
      },
      {
        '@type': 'City',
        name: 'Kirkland',
        containedInPlace: { '@type': 'State', name: 'Washington' },
      },
      {
        '@type': 'City',
        name: 'Seattle',
        containedInPlace: { '@type': 'State', name: 'Washington' },
      },
    ],

    serviceType: [
      'Custom Window Blinds',
      'Motorized Blinds Installation',
      'Automated Security Blinds',
      'Smart Blind Installation Redmond',
      'Window Shades Installation',
      'Window Treatment Consultation',
      'AR Window Treatment Visualization',
      'Smart Home Blinds Integration',
    ],

    knowsAbout: [
      'Custom Window Treatments',
      'Motorized Blinds',
      'Smart Home Integration',
      'Window Covering Installation',
      'Patent-pending Smart Defense Security Blinds',
    ],

    currenciesAccepted: 'USD',
    paymentAccepted: 'Credit Card, Debit Card',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Product Schema
interface ProductJsonLdProps {
  name: string;
  description: string;
  image: string;
  sku?: string;
  price: number;
  priceCurrency?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  url: string;
  brand?: string;
  rating?: number;
  reviewCount?: number;
  category?: string;
}

export function ProductJsonLd({
  name,
  description,
  image,
  sku,
  price,
  priceCurrency = 'USD',
  availability = 'InStock',
  url,
  brand,
  rating,
  reviewCount,
  category,
}: ProductJsonLdProps) {
  const cfg = useSiteConfig();

  const companyName = cfg.companyName || seoFallback.name;
  const siteUrl = cfg.siteUrl || seoFallback.url;
  const brandName = brand || companyName;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: image.startsWith('http') ? image : `${siteUrl}${image}`,
    sku: sku || name.toLowerCase().replace(/\s+/g, '-'),
    brand: {
      '@type': 'Brand',
      name: brandName,
    },
    category: category || 'Window Treatments',
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency,
      price: price.toFixed(2),
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: `https://schema.org/${availability}`,
      seller: {
        '@type': 'Organization',
        name: companyName,
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'USD',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 3,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 5,
            maxValue: 10,
            unitCode: 'DAY',
          },
        },
      },
    },
  };

  // Add aggregate rating if available
  if (rating && reviewCount && reviewCount > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: rating.toFixed(1),
      reviewCount,
      bestRating: '5',
      worstRating: '1',
    };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Breadcrumb Schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  const cfg = useSiteConfig();
  const siteUrl = cfg.siteUrl || seoFallback.url;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// FAQ Schema
interface FAQItem {
  question: string;
  answer: string;
}

export function FAQJsonLd({ items }: { items: FAQItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// How-To Schema (for guides)
interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

export function HowToJsonLd({
  name,
  description,
  steps,
  totalTime,
  image,
}: {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string; // ISO 8601 duration format, e.g., "PT30M"
  image?: string;
}) {
  const cfg = useSiteConfig();
  const siteUrl = cfg.siteUrl || seoFallback.url;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.image && {
        image: {
          '@type': 'ImageObject',
          url: step.image.startsWith('http') ? step.image : `${siteUrl}${step.image}`,
        },
      }),
    })),
  };

  if (totalTime) {
    schema.totalTime = totalTime;
  }

  if (image) {
    schema.image = {
      '@type': 'ImageObject',
      url: image.startsWith('http') ? image : `${siteUrl}${image}`,
    };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Review Schema
export function ReviewJsonLd({
  itemReviewed,
  reviewRating,
  author,
  reviewBody,
  datePublished,
}: {
  itemReviewed: { name: string; type?: string };
  reviewRating: number;
  author: string;
  reviewBody: string;
  datePublished: string;
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': itemReviewed.type || 'Product',
      name: itemReviewed.name,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: reviewRating,
      bestRating: '5',
      worstRating: '1',
    },
    author: {
      '@type': 'Person',
      name: author,
    },
    reviewBody,
    datePublished,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Service Schema (for measure & install)
export function ServiceJsonLd({
  name,
  description,
  provider,
  areaServed = 'United States',
  serviceType,
}: {
  name: string;
  description: string;
  provider?: string;
  areaServed?: string;
  serviceType?: string;
}) {
  const cfg = useSiteConfig();

  const companyName = cfg.companyName || seoFallback.name;
  const siteUrl = cfg.siteUrl || seoFallback.url;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: provider || companyName,
      url: siteUrl,
    },
    areaServed: {
      '@type': 'Country',
      name: areaServed,
    },
    serviceType: serviceType || name,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Collection/ItemList Schema (for product listings)
interface CollectionItem {
  name: string;
  url: string;
  image?: string;
  price?: number;
}

export function CollectionJsonLd({
  name,
  description,
  items,
}: {
  name: string;
  description?: string;
  items: CollectionItem[];
}) {
  const cfg = useSiteConfig();
  const siteUrl = cfg.siteUrl || seoFallback.url;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    description,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: item.name,
        url: item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`,
        ...(item.image && {
          image: item.image.startsWith('http') ? item.image : `${siteUrl}${item.image}`,
        }),
        ...(item.price && {
          offers: {
            '@type': 'Offer',
            price: item.price.toFixed(2),
            priceCurrency: 'USD',
          },
        }),
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
